import { createContext, useContext, ReactNode, JSX } from 'react';

import { useBusinessEntity } from '@zenbusiness/application-commons-business-entity';
import { useQuery, gql } from '@zenbusiness/application-commons-graphql';
import { useError } from '@zenbusiness/application-commons-logging';
import PageError from '@zenbusiness/zui/PageError';
import PageLoading from '@zenbusiness/zui/PageLoading';
import { useLocation } from 'react-router-dom';

import { DB_APPLICATION_STATUS, INITIAL_PAYMENT_STATUS } from '#client/constants/application';
import { RELEASE_SHOW_INSTANT_DEPOSIT } from '#client/constants/experimentsFlags';
import useApplicationStatusContext from '#client/hooks/useApplicationStatusContext';
import useBankingAccounts from '#client/hooks/useBankingAccounts';
import useFeatureFlag from '#client/hooks/useFeatureFlag';
import useMfaStatus from '#client/hooks/useMfaStatus';
import useOrderTaskContext from '#client/hooks/useOrderTaskContext';
import useUnitAccountId from '#client/hooks/useUnitAccountId';
import { DEPOSIT_MUTATION, TRANSFER_MUTATION, WITHDRAW_MUTATION } from '#client/mutations';

const BankingDataContext = createContext(null);
BankingDataContext.displayName = 'BankingDataContext';

export const BANKING_DATA_QUERY = gql`
  query bankingBankingData(
    $businessEntityUuid: UUID!
    $skipBankAccount: Boolean!
    $skipInstantFundingAccounts: Boolean!
    $skipNextUpCards: Boolean!
  ) {
    bankAccount(input: { businessEntityUuid: $businessEntityUuid }) @skip(if: $skipBankAccount) {
      id
      accountNumber
      availableBalance {
        amount
        formattedAmount
      }
      balance {
        formattedAmount
      }
      balanceHistories(input: { businessEntityUuid: $businessEntityUuid }) {
        balance {
          amount
        }
        date
      }
      createdAt
      depositProduct
      hold {
        formattedAmount
      }
      name
      routingNumber
      status
    }
    bankingSubscription(input: { businessEntityUuid: $businessEntityUuid }) {
      subscriptionStatus
      termEndDate
      isMissedPayment
      firstMissedPaymentDate
    }
    instantFundingAccounts(businessEntityUuid: $businessEntityUuid)
      @skip(if: $skipInstantFundingAccounts) {
      businessProfileStatus
      isEligibleForTransfers
      accountStatus
      hasAcceptedTerms
      instantFundingCards {
        uuid
        instantFundingAccountUuid
        cardBillingAddress {
          address1
          address2
          state
          city
          country
        }
        cardStatus
        cardReviewStatus
        cardCompany
        cardType
        cardHolderFirstName
        cardHolderLastName
        cardLastFourDigits
        cardExpirationDate
        isCardExpired
        isCardPullEnabled
        isCardPushEnabled
        isSourceCard
        isInstantFundingCardRemoved
        isCardValidForTransfers
        cardCreatedAt
        cardUpdatedAt
      }
    }
    nextUpCards(params: { businessEntityUuid: $businessEntityUuid }) @skip(if: $skipNextUpCards) {
      nextUpCards {
        type
        order
      }
    }
  }
`;

const generateRefetchAfterMutations = (item = '') =>
  ['payees', 'connected-accounts'].includes(item?.toLocaleLowerCase())
    ? {
        refetchAfterMutations: [TRANSFER_MUTATION, DEPOSIT_MUTATION, WITHDRAW_MUTATION]
      }
    : {};

/**
 * BankingDataProvider
 * Fetches and manages banking account, instant funding accounts, and banking subscription and next up cards data and provides it via context.
 *
 * Renders:
 *   - A loading indicator while fetching data
 *   - An error page if fetching fails
 *   - Its children wrapped in BankingDataContext.Provider when data is ready
 *
 * @param {ReactNode} children - Children to render
 * @returns {JSX.Element} - BankingDataContext context
 */
function BankingDataProvider({ children }) {
  const { unitAccountId } = useUnitAccountId();
  const { businessEntityUuid } = useBusinessEntity();
  const { isEnabled, isEnrolled, isValidMfa } = useMfaStatus();
  const [isShowInstantDepositFlagEnabled, isShowInstantDepositFlagLoading] = useFeatureFlag(
    RELEASE_SHOW_INSTANT_DEPOSIT
  );
  const { pathname } = useLocation();
  const items = pathname?.split('/') || [];
  const lastItem = [...items].pop();

  const { id: bankingAccountId, loading: isFetchingBankingAccount } = useBankingAccounts();

  const { appStatus, initialPaymentStatus } = useApplicationStatusContext();
  const { bankingPreferredTask } = useOrderTaskContext();

  const hasFirstPaymentFailedCheck =
    appStatus === DB_APPLICATION_STATUS.APPROVED &&
    initialPaymentStatus === INITIAL_PAYMENT_STATUS.FAILED &&
    bankingPreferredTask?.status !== 'active';
  const skipBankAccount =
    appStatus !== DB_APPLICATION_STATUS.APPROVED ||
    hasFirstPaymentFailedCheck ||
    !bankingAccountId ||
    isFetchingBankingAccount;

  const skipInstantFundingAccounts =
    isShowInstantDepositFlagLoading ||
    !isShowInstantDepositFlagEnabled ||
    !isEnabled ||
    !isEnrolled ||
    !isValidMfa;

  const skipNextUpCards = !unitAccountId;

  const request = useQuery(BANKING_DATA_QUERY, {
    noThrow: true,
    variables: {
      businessEntityUuid,
      skipBankAccount,
      skipInstantFundingAccounts,
      skipNextUpCards
    },
    skipCache: true,
    ...generateRefetchAfterMutations(lastItem)
  });

  useError(request.error);

  if (request.error) {
    return <PageError />;
  }
  if (request.loading) {
    return <PageLoading />;
  }

  return <BankingDataContext.Provider value={request}>{children}</BankingDataContext.Provider>;
}

function useBankingDataContext() {
  const context = useContext(BankingDataContext);
  if (!context) {
    throw new Error('useBankingDataContext must be used within a BankingDataProvider');
  }
  return context;
}

export { BankingDataProvider, useBankingDataContext };
